// Shared input and rendering for the two drawing surfaces.
export type InkPoint = [number, number, number];
export interface InkStroke { points: InkPoint[]; color: string; size: number }

export function acceptsInk(e: PointerEvent): boolean {
  return e.pointerType !== 'touch' || localStorage.getItem('swatnotes-finger-drawing') === 'true';
}

export function inkSamples(e: PointerEvent): PointerEvent[] {
  const samples = e.getCoalescedEvents?.();
  return samples?.length ? samples : [e];
}

export function inkPoint(e: PointerEvent, svg: SVGSVGElement): InkPoint {
  const matrix = svg.getScreenCTM();
  const point = new DOMPoint(e.clientX, e.clientY);
  const local = matrix ? point.matrixTransform(matrix.inverse()) : point;
  const pressure = e.pointerType === 'pen' ? e.pressure : 0.5;
  return [local.x, local.y, Math.max(0.01, Math.min(1, pressure))];
}

export function finishInkStroke(stroke: InkStroke, e: PointerEvent, svg: SVGSVGElement) {
  // Cancellation/capture loss can report zero coordinates. Keep the sampled ink
  // without inventing a line to the origin. A real lift-off has a final position.
  if (e.type !== 'pointerup') return;
  const last = stroke.points.at(-1);
  const point = inkPoint(e, svg);
  if (!last || (last[0] === point[0] && last[1] === point[1])) return;
  // Pen pressure normally becomes zero at lift-off; preserve the last contact
  // pressure so the final segment doesn't collapse to an artificial hairline.
  if (e.pressure === 0) point[2] = last[2];
  stroke.points.push(point);
}

export function inkStrokeHits(stroke: InkStroke, x: number, y: number, radius: number): boolean {
  const limit = radius * radius;
  return stroke.points.some(([px, py], index, points) => {
    const [ax, ay] = points[Math.max(0, index - 1)]!;
    const dx = px - ax, dy = py - ay;
    const length = dx * dx + dy * dy;
    const t = length ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / length)) : 0;
    return (x - ax - t * dx) ** 2 + (y - ay - t * dy) ** 2 <= limit;
  });
}

// Keep completed SVG paths intact; only recompute the active stroke, once per
// animation frame. Sampling remains independent of the display refresh rate.
export function inkRenderer(
  id: string, pathFor: (stroke: InkStroke) => string,
  snapshot: () => { strokes: InkStroke[]; current: InkStroke | null },
): () => void {
  let frame = 0;
  const paths = new Map<InkStroke, SVGPathElement>();
  const lengths = new WeakMap<InkStroke, number>();
  return () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      const svg = document.getElementById(id)!;
      const { strokes, current } = snapshot();
      const visible = new Set(current ? [...strokes, current] : strokes);
      for (const [stroke, path] of paths) {
        if (!visible.has(stroke)) { path.remove(); paths.delete(stroke); }
      }
      for (const stroke of visible) {
        let path = paths.get(stroke);
        if (!path) {
          path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          path.setAttribute('data-ink', '1');
          path.setAttribute('fill', stroke.color);
          path.setAttribute('d', pathFor(stroke));
          paths.set(stroke, path);
          lengths.set(stroke, stroke.points.length);
          svg.appendChild(path);
        } else if (stroke === current || lengths.get(stroke) !== stroke.points.length) {
          path.setAttribute('d', pathFor(stroke));
          lengths.set(stroke, stroke.points.length);
        }
      }
    });
  };
}

export function wireFingerDrawing() {
  document.querySelectorAll<HTMLInputElement>('[data-finger-drawing]').forEach(input => {
    input.checked = localStorage.getItem('swatnotes-finger-drawing') === 'true';
    input.addEventListener('change', () => {
      localStorage.setItem('swatnotes-finger-drawing', String(input.checked));
      document.querySelectorAll<HTMLInputElement>('[data-finger-drawing]').forEach(other => { other.checked = input.checked; });
    });
  });
}
